import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import T from 'T'
import HHAccordion from 'components/common/HHAccordion'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  useTheme,
  List,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Button,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import { useLazyGetStatementListByAccountQuery } from 'api/billing/statementCrud'
import { get } from 'lodash'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import { accordionSummaryClasses } from '@mui/material/AccordionSummary'
import StatementListItem from 'components/customer-details/content/statements/StatementListItem'
import StatementDrawer from 'components/customer-details/content/statements/StatementDrawer'
import StatementActionMenu from 'components/customer-details/content/statements/StatementActionMenu'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import GenerateStatementDialog from 'components/customer-details/content/statements/GenerateStatementDialog'
import EventBlocker from 'components/common/EventBlocker'
import { groupStatementsByYear } from './settings'

const Statements = ({ accountId, customerDetails }) => {
  const theme = useTheme()
  const [getStatementList, { isLoading, isFetching, data, isSuccess }] = useLazyGetStatementListByAccountQuery()
  const [isStatementDrawerOpen, setIsStatementDrawerOpen] = useState(false)
  const [isGenerateStatementDialogOpen, setIsGenerateStatementDialogOpen] = useState(false)
  const [currentStatement, setCurrentStatement] = useState()
  const [anchorEl, setAnchorEl] = useState()
  const loading = useMemo(() => isLoading || isFetching, [isLoading, isFetching])
  const shimmersArray = new Array(5).fill(0)
  const accountName = useMemo(() => get(customerDetails, 'accountName', ''), [customerDetails])

  const openActionMenu = e => setAnchorEl(e.currentTarget)

  const closeActionMenu = () => setAnchorEl(null)

  const openStatementDrawer = () => {
    setIsStatementDrawerOpen(true)
  }

  const closeStatementDrawer = () => {
    setCurrentStatement(undefined)
    setIsStatementDrawerOpen(false)
  }

  const openGenerateStatementDialog = () => {
    setIsGenerateStatementDialogOpen(true)
  }

  const closeGenerateStatementDialog = () => {
    setIsGenerateStatementDialogOpen(false)
  }

  const groupedStatements = useMemo(() => {
    const statements = get(data, 'statements', [])
    return groupStatementsByYear(statements)
  }, [data])
  const groupedStatementsKeys = useMemo(() => Object.keys(groupedStatements), [groupedStatements])
  const hasGroupedStatements = groupedStatementsKeys.length > 0

  const currentYear = new Date().getFullYear().toString()

  const [expandedYears, setExpandedYears] = useState(() => {
    const initialExpanded = {}
    groupedStatementsKeys.forEach(year => {
      initialExpanded[year] = year === currentYear
    })
    return initialExpanded
  })

  const toggleYearExpansion = year => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year],
    }))
  }

  const createStatement = () => {
    openGenerateStatementDialog()
  }

  useEffect(() => {
    if (accountId) {
      getStatementList({ accountId })
    }
  }, [accountId])

  return (
    <>
      <HHAccordion
        withNewColors
        isExpanded
        isExpandCollapseHeightSame
        summary={`${T.STATEMENT}s`}
        showActionIcon={false}
        showCustomActions
        customActionContent={
          <EventBlocker>
            <Button onClick={createStatement} variant="outlined" size="small">
              {T.CREATE_STATEMENT}
            </Button>
          </EventBlocker>
        }
      >
        {loading && (
          <List disablePadding>
            {shimmersArray.map(() => (
              <ListItem>
                <ListItemIcon>
                  <ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />
                </ListItemIcon>
                <ListItemText
                  primary={<Skeleton variant="text" width={150} />}
                  slotProps={{
                    primary: { variant: 'h5' },
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}
        {isSuccess && !loading && !hasGroupedStatements && <HHSectionPlaceholder title={T.NO_STATEMENTS} />}
        {hasGroupedStatements &&
          isSuccess &&
          !loading &&
          groupedStatementsKeys.map(year => {
            const statements = groupedStatements[year]
            const statementsCount = statements.length
            const expanded = expandedYears[year]
            const onCurrentYearChange = () => toggleYearExpansion(year)
            return (
              <Accordion
                key={`statement-accordion-item-${year}`}
                expanded={expanded}
                onChange={onCurrentYearChange}
                elevation={0}
                disableGutters
              >
                <AccordionSummary
                  expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
                  sx={{
                    flexDirection: 'row-reverse',
                    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
                      transform: 'rotate(90deg)',
                    },
                    [`& .${accordionSummaryClasses.content}`]: {
                      marginLeft: theme.spacing(2),
                    },
                  }}
                >
                  <Grid container sx={{ width: '100%' }} alignItems="center">
                    <Grid size="grow">
                      <Typography variant="h5" fontWeight={700}>
                        {year}
                      </Typography>
                    </Grid>
                    <Grid>
                      <Typography variant="h6" fontWeight={500}>
                        {statementsCount}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: 0,
                  }}
                >
                  <Divider />
                  <List disablePadding>
                    {statements.map(statement => {
                      const handleStatementClick = () => {
                        setCurrentStatement(statement)
                        openStatementDrawer()
                      }

                      const handleStatementActionClick = e => {
                        setCurrentStatement(statement)
                        openActionMenu(e)
                      }

                      return (
                        <StatementListItem
                          onClick={handleStatementClick}
                          onClickActionMenu={handleStatementActionClick}
                          statement={statement}
                          key={`statement-item-${statement.id}`}
                        />
                      )
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>
            )
          })}
      </HHAccordion>
      <EventBlocker>
        <StatementDrawer
          open={isStatementDrawerOpen}
          onClose={closeStatementDrawer}
          statementNumber={currentStatement?.statementNumber}
          customerDetails={customerDetails}
          accountName={accountName}
        />
        <StatementActionMenu
          anchorEl={anchorEl}
          statement={currentStatement}
          statementNumber={currentStatement?.statementNumber}
          customerDetails={customerDetails}
          onClose={closeActionMenu}
        />
        <GenerateStatementDialog
          open={isGenerateStatementDialogOpen}
          onClose={closeGenerateStatementDialog}
          customerDetails={customerDetails}
        />
      </EventBlocker>
    </>
  )
}

Statements.propTypes = {
  accountId: PropTypes.string,
  customerDetails: PropTypes.object,
}

export default Statements
